import enums from "@/helpers/enums";

const RESIDENT_SPECIES = "NRKW";
const TRANSIENT_SPECIES = "BIGGS";
const UNKNOWN_ORCA_SPECIES = "Oo";

const ORCA_SPECIES = [RESIDENT_SPECIES, TRANSIENT_SPECIES, UNKNOWN_ORCA_SPECIES];

function isOrca(species) {
  return ORCA_SPECIES.includes(species);
}

function forcedEcotype(species) {
  if (species === RESIDENT_SPECIES) {
    return "Resident";
  }
  if (species === TRANSIENT_SPECIES) {
    return "Transient";
  }
  return null;
}

function ecotypeIsLocked(species) {
  return forcedEcotype(species) !== null;
}

function clanEnabled(species, ecotype) {
  const effectiveEcotype = forcedEcotype(species) || ecotype;
  return isOrca(species) && effectiveEcotype === "Resident";
}

function clanOptions(species, ecotype) {
  if (!clanEnabled(species, ecotype)) {
    return [];
  }
  if (species === UNKNOWN_ORCA_SPECIES) {
    return enums.orcaClan.ALL_RESIDENT;
  }
  return enums.orcaClan.NORTHERN_RESIDENT;
}

function podOptions(clan) {
  return enums.clanPods[clan] || [];
}

function clanForPod(pod) {
  const clans = Object.keys(enums.clanPods);
  for (let i = 0; i < clans.length; i += 1) {
    if (enums.clanPods[clans[i]].includes(pod)) {
      return clans[i];
    }
  }
  return null;
}

function speciesLabel(species) {
  return species === UNKNOWN_ORCA_SPECIES ? "Unknown Orca (Oo)" : species;
}

function findContradiction(record) {
  const species = record.species;
  const ecotype = record.ecotype;
  const clan = record.clan;
  const pod = record.pod;

  if (!isOrca(species)) {
    return null;
  }

  const required = forcedEcotype(species);
  if (required && ecotype && ecotype !== required) {
    return {
      type: "ecotype",
      message: `Species is ${speciesLabel(species)} but ecotype is ${ecotype}.`,
      options: [
        {
          label: `Set ecotype to ${required} (match species)`,
          changes: { ecotype: required },
        },
        {
          label: "Set species to Unknown Orca (Oo) (match ecotype)",
          changes: { species: UNKNOWN_ORCA_SPECIES },
        },
      ],
    };
  }

  if (clan) {
    const allowed = clanOptions(species, ecotype);
    if (!allowed.includes(clan)) {
      const options = [
        {
          label: "Clear the clan (and pod)",
          changes: { clan: "", pod: "" },
        },
      ];
      if (
        enums.orcaClan.ALL_RESIDENT.includes(clan) &&
        !enums.orcaClan.NORTHERN_RESIDENT.includes(clan)
      ) {
        options.push({
          label: "Set species to Unknown Orca (Oo)",
          changes: { species: UNKNOWN_ORCA_SPECIES, ecotype: "Resident" },
        });
      }
      return {
        type: "clan",
        message: `Clan ${clan} is not valid for species ${speciesLabel(species)}${
          ecotype ? ` with ecotype ${ecotype}` : ""
        }.`,
        options,
      };
    }
  }

  if (pod) {
    const owningClan = clanForPod(pod);
    if (owningClan === null) {
      return {
        type: "pod",
        message: `Pod ${pod} does not belong to any known clan.`,
        options: [
          {
            label: "Clear the pod",
            changes: { pod: "" },
          },
        ],
      };
    }
    if (clan && owningClan !== clan) {
      return {
        type: "pod",
        message: `Pod ${pod} belongs to clan ${owningClan}, but the clan is set to ${clan}.`,
        options: [
          {
            label: "Clear the pod",
            changes: { pod: "" },
          },
          {
            label: `Change clan to ${owningClan} (the clan that owns pod ${pod})`,
            changes: { clan: owningClan },
          },
        ],
      };
    }
  }

  return null;
}

export default {
  isOrca,
  forcedEcotype,
  ecotypeIsLocked,
  clanEnabled,
  clanOptions,
  podOptions,
  clanForPod,
  findContradiction,
};
